import * as d3 from 'd3';
import { TDrawModel } from '../../draw.model';
import { TStripElement } from '../../timeline.types';
import { DRAW_MODEL_UTILS } from '../utils';

interface StripPosition {
  el: TStripElement;
  x: number;
  xEnd: number;
  y: number;
}

interface LinkData {
  x1: number;
  x2: number;
  y: number;
}

/**
 * Calculate position data for strips
 */
const calculateStripPositions = (
  elements: TDrawModel['timelineModel']['entities'][number]['entities'][number]['elements'],
  drawModel: TDrawModel,
): StripPosition[] => {
  return elements
    .filter((el): el is TStripElement => el.type === 'STRIP')
    .map((el) => {
      const [start, end] = DRAW_MODEL_UTILS.getRangePosition(el.range, drawModel);
      const width = el.range.startDate === el.range.endDate && el.isHalfDay ? (end - start) / 2 : end - start;

      return {
        el,
        x: start - drawModel.layout.widthOfDay / 2,
        xEnd: start + width - drawModel.layout.widthOfDay / 2,
        y: DRAW_MODEL_UTILS.calculateElementYPosition(el, drawModel),
      };
    });
};

/**
 * Generate link data between connected strips
 */
const generateLinkData = (strips: StripPosition[], drawModel: TDrawModel): LinkData[] => {
  return strips
    .map((strip, i) => {
      if (strip.el.linkedToPrevious && i > 0) {
        return {
          x1: strips[i - 1].xEnd,
          x2: strip.x,
          y: strip.y + drawModel.layout.stackHeights[strip.el.stackCount],
        };
      }
      return null;
    })
    .filter((d): d is LinkData => d !== null);
};

/**
 * Generate SVG path for rounded U-shaped connector
 */
const generateLinkPath = ({ x1, x2, y }: LinkData): string => {
  const height = 10;
  const radius = 2;
  const minWidth = 3;
  const startX = x2 - x1 < minWidth ? x1 - minWidth / 2 : x1;

  // Draws a rounded U-shaped connector with rounded bottom corners between two strips:
  // └────────┘
  return `M${startX},${y}
          v${height - radius}
          a${radius},${radius} 0 0 0 ${radius},${radius}
          h${Math.max(x2 - x1, minWidth) - 2 * radius}
          a${radius},${radius} 0 0 0 ${radius},-${radius}
          v-${height - radius}`;
};

/**
 * Check if any entity has strips with linkedToPrevious
 */
const hasLinkedStrips = (drawModel: TDrawModel): boolean => {
  return drawModel.timelineModel.entities.some((entityGroup) =>
    entityGroup.entities.some((entity) =>
      entity.elements.some((element) => element.type === 'STRIP' && element.linkedToPrevious),
    ),
  );
};

/**
 * Main function to draw strip links
 */
export const drawStripLinks = (
  entity: d3.Selection<
    d3.BaseType | SVGGElement,
    TDrawModel['timelineModel']['entities'][number]['entities'][number],
    d3.BaseType | SVGSVGElement,
    TDrawModel['timelineModel']['entities'][number]
  >,
  drawModel: TDrawModel,
) => {
  // Early return if no strips have linkedToPrevious
  if (!hasLinkedStrips(drawModel)) {
    // Clean up any existing links
    entity.selectAll('.element-link').remove();
    return;
  }

  entity
    .selectAll('.element-link')
    .data((d) => {
      const strips = calculateStripPositions(d.elements, drawModel);
      return generateLinkData(strips, drawModel);
    })
    .join('path')
    .classed('element-link', true)
    .attr('d', generateLinkPath)
    .attr('stroke', 'black')
    .attr('fill', 'none')
    .attr('stroke-width', 1.5);
};

export const DRAW_LINKS = {
  drawStripLinks,
};
